import type { BetaSeverity, BetaTester } from '@nolens/domain';
import { Banner, Button, Field, Input, Logo, Select, Textarea, useLocale } from '@nolens/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, apiForm, ApiError } from '../lib/api';
import { publicPaths } from '../i18n/landing';
import { reportText } from '../i18n/report';

const MAX_FILES = 5;
const MAX_FILE = 10 * 1024 * 1024;
const MAX_TOTAL = 25 * 1024 * 1024;
const TYPES = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];
const EMPTY = {
  email: '',
  title: '',
  severity: 'annoying' as BetaSeverity,
  description: '',
  steps: '',
  pageUrl: '',
};

/**
 * Anomalias e sugestões de quem testa (sem conta). Pela ligação pessoal do email de
 * boas-vindas (/reportar/<código>) já sabemos quem é; sem ela, pede o email da inscrição.
 */
export function ReportPage() {
  const { token } = useParams();
  const locale = useLocale();
  const t = reportText(locale);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const tester = useQuery({
    queryKey: ['beta-tester', token],
    queryFn: () => api<BetaTester>(`/public/beta-testers/${token}`, { anonymous: true }),
    enabled: !!token,
    retry: false,
  });
  // Ligação inválida: continua a funcionar, pedindo o email.
  const known = !!token && tester.isSuccess;
  const send = useMutation({
    mutationFn: () => {
      const body = new FormData();
      if (known) body.set('token', token);
      else body.set('email', form.email);
      body.set('title', form.title);
      body.set('severity', form.severity);
      body.set('description', form.description);
      body.set('steps', form.steps);
      body.set('pageUrl', form.pageUrl);
      body.set('browser', navigator.userAgent.slice(0, 300));
      for (const f of files) body.append('files', f, f.name);
      return apiForm('/public/beta-reports', body);
    },
  });
  useEffect(() => {
    const before = document.title;
    document.title = t.docTitle;
    return () => {
      document.title = before;
    };
  }, [t.docTitle]);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const addFiles = (list: FileList | null) => {
    const next = [...files, ...Array.from(list ?? [])];
    const bad = next.find((f) => !TYPES.includes(f.type));
    const big = next.find((f) => f.size > MAX_FILE);
    if (bad) setFileError(t.badType(bad.name));
    else if (big) setFileError(t.tooBig(big.name));
    else if (next.length > MAX_FILES) setFileError(t.tooManyFiles);
    else if (next.reduce((n, f) => n + f.size, 0) > MAX_TOTAL) setFileError(t.tooBigTotal);
    else {
      setFileError('');
      setFiles(next);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate();
  };
  const reset = () => {
    setForm({ ...EMPTY, email: form.email });
    setFiles([]);
    send.reset();
  };

  return (
    <main className="mx-auto max-w-xl px-5 py-10" data-report>
      <Link to={publicPaths(locale).product} aria-label="Nolens Guides">
        <Logo />
      </Link>
      <p className="mt-10 text-[14px] font-medium text-accent-text">{t.eyebrow}</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight">{t.title}</h1>
      <p className="mt-3 text-text-2">{t.lead}</p>
      {known && (
        <p className="mt-3 text-[13.5px] text-text-3" data-report-as>
          {t.reportingAs(
            tester.data.name ? `${tester.data.name} (${tester.data.email})` : tester.data.email,
          )}
        </p>
      )}
      {token && tester.isError && (
        <Banner tone="attention" className="mt-4">
          {t.unknownLink}
        </Banner>
      )}
      {send.isSuccess ? (
        <div className="mt-8" data-report-done>
          <Banner tone="positive">
            <strong>{t.doneTitle}</strong> {t.doneText}
          </Banner>
          <Button variant="secondary" className="mt-6" onClick={reset}>
            {t.another}
          </Button>
        </div>
      ) : (
        <form className="mt-8 grid grid-cols-1 gap-4" onSubmit={submit}>
          {!known && (
            <Field label={t.email}>
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={set('email')}
                />
              )}
            </Field>
          )}
          <Field label={t.titleLabel}>
            {(p) => (
              <Input
                {...p}
                required
                maxLength={200}
                placeholder={t.titlePlaceholder}
                value={form.title}
                onChange={set('title')}
              />
            )}
          </Field>
          <Field label={t.severity}>
            {(p) => (
              <Select {...p} value={form.severity} onChange={set('severity')}>
                <option value="blocker">{t.blocker}</option>
                <option value="annoying">{t.annoying}</option>
                <option value="suggestion">{t.suggestion}</option>
              </Select>
            )}
          </Field>
          <Field label={t.description}>
            {(p) => (
              <Textarea
                {...p}
                required
                rows={5}
                maxLength={5000}
                placeholder={t.descriptionPlaceholder}
                value={form.description}
                onChange={set('description')}
              />
            )}
          </Field>
          <Field label={t.steps}>
            {(p) => (
              <Textarea
                {...p}
                rows={4}
                maxLength={5000}
                placeholder={t.stepsPlaceholder}
                value={form.steps}
                onChange={set('steps')}
              />
            )}
          </Field>
          <Field label={t.pageUrl}>
            {(p) => (
              <Input
                {...p}
                maxLength={500}
                placeholder={t.pageUrlPlaceholder}
                value={form.pageUrl}
                onChange={set('pageUrl')}
              />
            )}
          </Field>
          <Field label={t.files} hint={t.filesHint} error={fileError || undefined}>
            {(p) => (
              <input
                {...p}
                type="file"
                multiple
                accept={TYPES.join(',')}
                disabled={files.length >= MAX_FILES}
                className="text-sm text-text-2 file:mr-3 file:rounded-full file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-text-1"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            )}
          </Field>
          {files.length > 0 && (
            <ul className="-mt-2 flex flex-col gap-1.5" data-report-files={files.length}>
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center gap-2 rounded-[10px] bg-surface-1 px-3 py-1.5 text-[13.5px] shadow-[inset_0_0_0_1px_var(--border)]"
                >
                  <span className="min-w-0 flex-1 truncate">{f.name}</span>
                  <span className="tabular-nums text-text-3">{t.size(f.size)}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t.removeFile(f.name)}
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}
                  >
                    ×
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {send.error instanceof ApiError && <Banner tone="critical">{send.error.message}</Banner>}
          <Button type="submit" size="lg" loading={send.isPending}>
            {t.submit}
          </Button>
          <p className="text-[12.5px] text-text-3">{t.privacy}</p>
        </form>
      )}
    </main>
  );
}
